/* Shared cart mutations. Never retries an add: a lost response can still have committed. */
(() => {
  'use strict';
  const route = path => `${window.Shopify?.routes?.root || '/'}${path.replace(/^\//, '')}`;
  const text = key => window.StreetTrackText?.[key] || key;
  const localURL = value => { if (typeof value !== 'string' || !value || value.length > 2048) return null; try { const url = new URL(value, location.origin); return url.origin === location.origin && /^https?:$/.test(url.protocol) ? url : null; } catch { return null; } };
  const imageURL = value => { if (typeof value !== 'string' || !value || value.length > 2048) return ''; try { const url = new URL(value, location.origin); return /^https?:$/.test(url.protocol) && (url.origin === location.origin || /(^|\.)shopify(cdn)?\.(com|net)$/.test(url.hostname)) ? url.href : ''; } catch { return ''; } };
  async function request(url, options = {}) {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    const data = await response.json().catch(() => null);
    if (!response.ok || !data) throw new Error(data?.description || data?.message || text('request_error'));
    return data;
  }
  const post = (path, data) => request(route(path), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(data) });
  const get = () => request(route('cart.js'), { cache: 'no-store' });
  let tail = Promise.resolve();
  const run = task => { const current = tail.then(task); tail = current.catch(() => {}); return current; };
  const emit = cart => document.dispatchEvent(new CustomEvent('cart:update', { detail: { source: 'streettrack-v3', sourceId: 'streettrack-v3', data: { source: 'streettrack-v3' }, cart } }));
  const properties = values => Object.fromEntries(Object.entries(values || {}).sort(([a], [b]) => a.localeCompare(b)));
  const plan = item => String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || '');
  const signature = item => JSON.stringify([String(item.variant_id || item.id), plan(item), properties(item.properties)]);
  const findLine = (cart, key, previous) => cart.items.find(item => item.key === key) || (previous && cart.items.filter(item => signature(item) === signature(previous)).length === 1 ? cart.items.find(item => signature(item) === signature(previous)) : null);
  function quantityValid(quantity, rule = {}) {
    const min = Number(rule.min) || 1, increment = Number(rule.increment) || 1, max = Number(rule.max) || Infinity;
    return Number.isInteger(quantity) && quantity >= min && quantity <= max && quantity % increment === 0;
  }
  async function changed(task) {
    try { const result = await task(); emit(result.cart || result); return result; }
    catch (error) { try { emit(await get()); } catch {} throw error; }
  }
  const change = (key, quantity) => run(() => changed(async () => {
    if (!Number.isInteger(quantity) || quantity < 0) throw new Error(text('quantity_error'));
    const cart = await get(), item = cart.items.find(line => line.key === key);
    if (!item) throw new Error(text('cart_changed'));
    if (quantity && !quantityValid(quantity, item.quantity_rule)) throw new Error(text('quantity_error'));
    return post('cart/change.js', { id: key, quantity });
  }));
  const add = form => run(() => changed(async () => {
    const item = await request(route('cart/add.js'), { method: 'POST', headers: { Accept: 'application/json' }, body: form });
    return { item, cart: await get() };
  }));
  const discount = code => run(() => changed(() => post('cart/update.js', { discount: code })));
  const replace = (key, variantId, sellingPlan) => run(() => changed(async () => {
    const before = await get(), old = before.items.find(item => item.key === key);
    if (!old) throw new Error(text('cart_changed'));
    if (old.has_components || old.parent_relationship) throw new Error(text('bundle_error'));
    if (String(old.variant_id || old.id) === String(variantId) && plan(old) === String(sellingPlan || '')) return { cart: before, item: old };
    const replacement = { id: variantId, quantity: old.quantity, properties: old.properties || {} };
    if (sellingPlan) replacement.selling_plan = sellingPlan;
    const added = await post('cart/add.js', replacement);
    let after;
    try {
      after = await get();
      const currentOld = findLine(after, old.key, old);
      if (!currentOld || currentOld.quantity !== old.quantity) throw new Error(text('cart_changed'));
      const cart = await post('cart/change.js', { id: currentOld.key, quantity: 0 });
      return { cart, item: cart.items.find(line => line.key === added.key) || added };
    } catch (error) {
      // Undo only the quantity this operation added, retaining an existing target line.
      try {
        after = await get();
        const currentNew = findLine(after, added.key, { ...replacement, variant_id: variantId });
        // The remove response may have been lost after Shopify committed it.
        // A missing original with an identifiable replacement is a completed edit.
        if (!findLine(after, old.key, old) && currentNew) return { cart: after, item: currentNew };
        if (!currentNew || currentNew.quantity < old.quantity) throw new Error('Cannot identify replacement');
        await post('cart/change.js', { id: currentNew.key, quantity: currentNew.quantity - old.quantity });
      } catch { throw new Error(text('edit_recovery_error')); }
      throw error;
    }
  }));
  window.StreetTrackCart = { route, request, post, get, run, change, add, replace, discount, emit, localURL, imageURL, quantityValid, signature, text };
})();
